import { useEffect, useState } from "react";
import { ChevronDown, ChevronUp, SlidersHorizontal, X } from "lucide-react";
import { t } from "@/lib/i18n";
import type { LogFilters, LogLevel, TimeRange } from "@/lib/types";
import { LEVELS } from "@/lib/types";
import { cn } from "@/lib/utils";

const RANGE_LABELS: [TimeRange, string][] = [
  ["15m", t("Letzte 15 Min")],
  ["1h", t("Letzte Stunde")],
  ["6h", t("Letzte 6 Std")],
  ["24h", t("Letzte 24 Std")],
  ["7d", t("Letzte 7 Tage")],
  ["custom", t("Benutzerdefiniert…")],
];

type Suggestions = {
  clients: string[];
  services: string[];
  hosts: string[];
};

type FilterBarProps = {
  filters: LogFilters;
  onChange: (patch: Partial<LogFilters>) => void;
  onReset: () => void;
  suggestions: Suggestions;
};

/**
 * Filterleiste.
 *
 * Desktop (≥ md): alle Hauptfilter in einer umbrechenden Zeile, „Erweitert"
 * klappt Service/Host/Trace auf.
 *
 * Mobil: nur Level + Nachricht + „Filter"-Button sichtbar. Der Button klappt
 * Client, Zeitraum, Limit (als eigene Zeilen unter der Hauptzeile, per
 * flex-order) *und* die erweiterten Felder auf. So bleibt der Kopf auf dem
 * Handy zwei Zeilen hoch, statt sechs Felder untereinander zu stapeln.
 */
export function FilterBar({ filters, onChange, onReset, suggestions }: FilterBarProps) {
  const [expanded, setExpanded] = useState(false);
  const [search, setSearch] = useState(filters.search ?? "");
  const [client, setClient] = useState(filters.client ?? "");

  useEffect(() => setSearch(filters.search ?? ""), [filters.search]);
  useEffect(() => setClient(filters.client ?? ""), [filters.client]);

  // Debounce für Freitext-Filter, damit nicht jedes Tastendruck-WS-Roundtrip
  useEffect(() => {
    const t = window.setTimeout(() => {
      if ((filters.search ?? "") !== search) onChange({ search: search || undefined });
    }, 250);
    return () => window.clearTimeout(t);
  }, [search]);

  useEffect(() => {
    const t = window.setTimeout(() => {
      if ((filters.client ?? "") !== client) onChange({ client: client || undefined });
    }, 250);
    return () => window.clearTimeout(t);
  }, [client]);

  const activeCount =
    (filters.level ? 1 : 0) +
    (filters.client ? 1 : 0) +
    (filters.search ? 1 : 0) +
    (filters.service ? 1 : 0) +
    (filters.host ? 1 : 0) +
    (filters.traceId ? 1 : 0) +
    (filters.range ? 1 : 0);

  // Mobil erst nach „Filter" sichtbar; auf Desktop immer in der Hauptzeile.
  const secondary = expanded ? "" : "hidden md:flex";

  return (
    <div className="mb-3 rounded-md border border-border bg-card p-2.5 md:mb-4 md:p-3">
      <div className="flex flex-wrap items-end gap-2 md:gap-3">
        <Field label={t("Level")} className="w-[5.5rem] md:w-auto">
          <select
            value={filters.level ?? ""}
            onChange={(e) => onChange({ level: (e.target.value || undefined) as LogLevel | undefined })}
            className={selectCls}
          >
            <option value="">{t("Alle")}</option>
            {LEVELS.map((l) => (
              <option key={l} value={l}>
                {l}
              </option>
            ))}
          </select>
        </Field>

        <Field
          label={t("Client")}
          className={cn("order-4 basis-full md:order-none md:min-w-[180px] md:flex-1 md:basis-auto", secondary)}
        >
          <input
            type="text"
            value={client}
            list="leif-client-suggestions"
            onChange={(e) => setClient(e.target.value)}
            placeholder={t("exakt oder sensor* (Platzhalter)")}
            className={inputCls}
          />
        </Field>

        <Field label={t("Nachricht")} className="min-w-0 flex-1 md:min-w-[220px] md:flex-[2]">
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("Suchen… (* als Platzhalter)")}
            className={inputCls}
          />
        </Field>

        <Field
          label={t("Zeitraum")}
          className={cn("order-5 basis-[calc(50%-0.25rem)] md:order-none md:basis-auto", secondary)}
        >
          <select
            value={filters.range ?? ""}
            onChange={(e) => {
              const range = (e.target.value || undefined) as TimeRange | undefined;
              // Preset/Live gewählt → alte custom-Grenzen mit ausräumen.
              onChange(
                range === "custom"
                  ? { range }
                  : { range, from: undefined, to: undefined },
              );
            }}
            className={selectCls}
          >
            <option value="">{t("Live")}</option>
            {RANGE_LABELS.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </Field>

        <Field
          label={t("Limit")}
          className={cn("order-6 basis-[calc(50%-0.25rem)] md:order-none md:basis-auto", secondary)}
        >
          <select
            value={filters.limit ?? 100}
            onChange={(e) => onChange({ limit: parseInt(e.target.value, 10) })}
            className={selectCls}
          >
            {[50, 100, 250, 500].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </Field>

        {filters.range === "custom" ? (
          <>
            <Field
              label={t("Von")}
              className={cn("order-7 basis-full md:order-none md:basis-auto", secondary)}
            >
              <input
                type="datetime-local"
                value={filters.from ?? ""}
                onChange={(e) => onChange({ from: e.target.value || undefined })}
                className={inputCls}
              />
            </Field>
            <Field
              label={t("Bis")}
              className={cn("order-7 basis-full md:order-none md:basis-auto", secondary)}
            >
              <input
                type="datetime-local"
                value={filters.to ?? ""}
                onChange={(e) => onChange({ to: e.target.value || undefined })}
                className={inputCls}
              />
            </Field>
          </>
        ) : null}

        <div className="order-3 ml-auto flex items-center gap-1 md:order-none md:gap-2">
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
            className="inline-flex h-10 items-center gap-1.5 rounded-md border border-border px-3 text-xs text-muted-foreground hover:bg-muted md:h-auto md:gap-1 md:px-2 md:py-1"
          >
            <SlidersHorizontal className="h-4 w-4 md:hidden" />
            {expanded ? (
              <ChevronUp className="hidden h-3.5 w-3.5 md:block" />
            ) : (
              <ChevronDown className="hidden h-3.5 w-3.5 md:block" />
            )}
            <span className="md:hidden">{t("Filter")}</span>
            <span className="hidden md:inline">{t("Erweitert")}</span>
            {activeCount > 0 ? (
              <span className="ml-1 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-medium text-primary-foreground">
                {activeCount}
              </span>
            ) : null}
          </button>
          {activeCount > 0 ? (
            <button
              type="button"
              onClick={onReset}
              aria-label={t("Zurücksetzen")}
              title={t("Zurücksetzen")}
              className="inline-flex h-10 w-10 items-center justify-center gap-1 rounded-md text-xs text-muted-foreground hover:text-foreground md:h-auto md:w-auto md:px-2 md:py-1"
            >
              <X className="h-4 w-4 md:h-3.5 md:w-3.5" />
              <span className="hidden md:inline">{t("Zurücksetzen")}</span>
            </button>
          ) : null}
        </div>
      </div>

      {expanded ? (
        <div
          className={cn(
            "mt-2 grid grid-cols-1 gap-2 border-t border-border pt-2 sm:grid-cols-2 md:mt-3 md:gap-3 md:pt-3 lg:grid-cols-4",
          )}
        >
          <Field label={t("Level-Modus")}>
            <select
              value={filters.levelMode ?? "minimum"}
              onChange={(e) => onChange({ levelMode: e.target.value as "minimum" | "exact" })}
              className={selectCls}
            >
              <option value="minimum">{t("Ab diesem Level ↑")}</option>
              <option value="exact">{t("Exakt dieses Level")}</option>
            </select>
          </Field>
          <Field label={t("Service")}>
            <input
              type="text"
              defaultValue={filters.service ?? ""}
              list="leif-service-suggestions"
              onChange={(e) => onChange({ service: e.target.value || undefined })}
              placeholder={t("z.B. auth-*")}
              className={inputCls}
            />
          </Field>
          <Field label={t("Host")}>
            <input
              type="text"
              defaultValue={filters.host ?? ""}
              list="leif-host-suggestions"
              onChange={(e) => onChange({ host: e.target.value || undefined })}
              placeholder={t("z.B. pod-prod-*")}
              className={inputCls}
            />
          </Field>
          <Field label={t("Trace ID")}>
            <input
              type="text"
              defaultValue={filters.traceId ?? ""}
              onChange={(e) => onChange({ traceId: e.target.value || undefined })}
              placeholder={t("exakte trace_id")}
              className={inputCls}
            />
          </Field>
        </div>
      ) : null}

      <datalist id="leif-client-suggestions">
        {suggestions.clients.map((c) => (
          <option key={c} value={c} />
        ))}
      </datalist>
      <datalist id="leif-service-suggestions">
        {suggestions.services.map((s) => (
          <option key={s} value={s} />
        ))}
      </datalist>
      <datalist id="leif-host-suggestions">
        {suggestions.hosts.map((h) => (
          <option key={h} value={h} />
        ))}
      </datalist>
    </div>
  );
}

// h-10 auf Touch (≥ 40px Ziel), h-8 auf Desktop wie bisher.
const inputCls =
  "h-10 w-full rounded-md border border-border bg-background px-2 text-sm outline-none transition-colors focus:border-primary focus:ring-1 focus:ring-primary md:h-8";
const selectCls = inputCls + " cursor-pointer";

function Field({
  label,
  children,
  className,
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <label className={cn("flex flex-col gap-1", className)}>
      <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      {children}
    </label>
  );
}
